import { useQuery, useMutation, UseQueryOptions, UseMutationOptions, useQueryClient } from '@tanstack/react-query';

export const useGetMemberDashboardSummary = (...args: any[]) => ({ data: { totalVisits: 0, nextAppointment: null } as any, isLoading: false, error: null as any });
export const getGetMemberDashboardSummaryQueryKey = (...args: any[]) => ['getMemberDashboardSummary'];

export const useGetMe = () => {
  const token = typeof window !== 'undefined' ? (localStorage.getItem('token') || '') : '';
  return useQuery({
    queryKey: ['getMe', token],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/auth/me`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch user profile');
      const json = await res.json();
      if (json.data) {
        localStorage.setItem("user", JSON.stringify(json.data));
      }
      return json.data;
    }
  });
};

// Members Check
export const useCheckMember = () => {
  return useMutation({
    mutationFn: async (payload: string | { memberId?: string; query?: string; firstName?: string; lastName?: string; email?: string; phone?: string; dob?: string }) => {
      let url = `${API_BASE}/members/check`;
      if (typeof payload === 'string') {
        const clean = payload.trim();
        url = `${API_BASE}/members/check?query=${encodeURIComponent(clean)}`;
      } else if (payload && typeof payload === 'object') {
        const params = new URLSearchParams();
        Object.entries(payload).forEach(([key, val]) => {
          if (val && typeof val === 'string' && val.trim()) {
            params.append(key, val.trim());
          }
        });
        url = `${API_BASE}/members/check?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to verify member');
      }
      return response.json();
    }
  });
};

export const useGetProvider = (id: string | number, options?: any) => {
  return useQuery({
    queryKey: ['provider', id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/providers/${id}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch provider');
      const json = await res.json();
      return json.data;
    },
    ...options?.query
  });
};
export const useAddCareTeamMember = () => {
  return useMutation({
    mutationFn: async (data: { providerId: string }) => {
      const res = await fetch(`${API_BASE}/care-team`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to add care team member');
      }
      return res.json();
    }
  });
};
export const getListCareTeamQueryKey = (...args: any[]) => ['getListCareTeam'];

export const useGetProviderDashboardSummary = (...args: any[]) => ({ data: { activePatients: 0, pendingAppointments: 0 } as any, isLoading: false, error: null as any });
export const getGetProviderDashboardSummaryQueryKey = (...args: any[]) => ['getProviderDashboardSummary'];

export const useListBundles = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: options?.query?.queryKey || ['getListBundles', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/bundles?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch bundles');
      const data = await res.json();
      return data.data;
    }
  });
};

export const useGetPublicBundle = (id: string, options?: any) => {
  return useQuery({
    queryKey: options?.query?.queryKey || ['getPublicBundle', id],
    queryFn: async () => {
      if (!id) return null;
      const res = await fetch(`${API_BASE}/bundles/public/${id}`, { headers: getHeaders() });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to fetch bundle');
      }
      const data = await res.json();
      return data.data;
    },
    enabled: !!id && (options?.query?.enabled ?? true),
    ...options?.query
  });
};

export const useListBundleCategories = (options?: { query?: any }) => {
  return useQuery({
    queryKey: ['bundle-categories'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/bundle-categories`, {
        headers: getHeaders()
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to fetch bundle categories');
      }
      const data = await res.json();
      return data.data || [];
    },
    ...options?.query
  });
};

export const useListFacilityTypes = (options?: { query?: any }) => {
  return useQuery({
    queryKey: ['facility-types'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/facility-types`, {
        headers: getHeaders()
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to fetch facility types');
      }
      const data = await res.json();
      return data.data || [];
    },
    ...options?.query
  });
};

export const getListBundlesQueryKey = (...args: any[]) => ['getListBundles'];

export const useCreateBundle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { data: any }) => {
      const res = await fetch(`${API_BASE}/bundles`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(variables.data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create bundle');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
    }
  });
};

export const useUpdateBundle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { id: string; data?: any; [key: string]: any }) => {
      const payload = variables.data ? variables.data : (() => {
        const { id, ...rest } = variables;
        return rest;
      })();
      const res = await fetch(`${API_BASE}/bundles/${variables.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update bundle');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
    }
  });
};

export const useDeleteBundle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { id: string | number }) => {
      const res = await fetch(`${API_BASE}/bundles/${variables.id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete bundle');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
    }
  });
};

export const useReorderBundles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: { id: string; sortOrder: number }[]) => {
      const res = await fetch(`${API_BASE}/bundles/reorder`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ items })
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to reorder bundles');
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
    }
  });
};

export const useListCareTeam = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: ['getListCareTeam', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/care-team?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch care team');
      return res.json();
    },
    ...options?.query
  });
};

export const useRemoveCareTeamMember = () => {
  return useMutation({
    mutationFn: async (data: { id: string }) => {
      const res = await fetch(`${API_BASE}/care-team/${data.id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to remove care team member');
      }
      return res.json();
    }
  });
};


export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocalHostOrIp = host === 'localhost' || host === '127.0.0.1' || /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host);
    if (isLocalHostOrIp) {
      return `http://${host}:4000/v1`;
    }
  }
  return 'https://api.straightdoc.info/v1';
};

export const API_BASE = getApiBaseUrl();
export const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export const useListMasterCptCodes = (params?: Record<string, any>) => {
  return useQuery({
    queryKey: ['getListMasterCptCodes', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/cpt/master?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch master CPT codes');
      const json = await res.json();
      return json.data;
    }
  });
};

export const useListCptCodes = (params?: Record<string, any>) => {
  return useQuery({
    queryKey: ['getListCptCodes', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/cpt/provider?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch provider CPT codes');
      const json = await res.json();
      return json.data;
    }
  });
};

export const useCreateCptCode = () => {
  return useMutation({
    mutationFn: async ({ data }: { data: any }) => {
      const res = await fetch(`${API_BASE}/cpt/provider`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to create CPT code');
      }
      const json = await res.json();
      return json.data;
    }
  });
};

export const useUpdateCptCode = () => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: any }) => {
      const res = await fetch(`${API_BASE}/cpt/provider/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to update CPT code');
      const json = await res.json();
      return json.data;
    }
  });
};

export const useDeleteCptCode = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: string | number }) => {
      const res = await fetch(`${API_BASE}/cpt/provider/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to delete CPT code');
      return json;
    }
  });
};

export const useClearAllCptCodes = () => {
  return useMutation({
    mutationFn: async (params?: { providerId?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.providerId && params.providerId !== 'all') {
        searchParams.append('providerId', params.providerId);
      }
      const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
      const res = await fetch(`${API_BASE}/cpt/provider/clear-all${qs}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to clear CPT codes');
      return json;
    }
  });
};

export const useGetLinkedProviders = (options?: any) => {
  return useQuery({
    queryKey: ['getLinkedProviders'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/cpt/provider/linked-providers`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch linked providers');
      const json = await res.json();
      return json.data;
    },
    ...options?.query
  });
};

export const getListCptCodesQueryKey = () => ['getListCptCodes'];

export const useListGfe = (...args: any[]) => ({ data: [] as any[], isLoading: false, error: null as any });
export const useCreateGfe = (...args: any[]) => ({ mutate: (...args: any[]) => { }, isPending: false });
export const useUpdateGfe = (...args: any[]) => ({ mutate: (...args: any[]) => { }, isPending: false });
export const getListGfeQueryKey = (...args: any[]) => ['getListGfe'];

export const getListNotificationsQueryKey = (...args: any[]) => ['getListNotifications'];

export const useListNotifications = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'> | any) => {
  return useQuery({
    queryKey: options?.query?.queryKey || getListNotificationsQueryKey(),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/notifications`, { headers: getHeaders() });
      if (!response.ok) throw new Error('Failed to fetch notifications');
      const json = await response.json();
      return json.data;
    },
    ...options?.query
  });
};

export const useMarkNotificationRead = (options?: UseMutationOptions<any, Error, string> | any) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, string>({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE}/notifications/${id}/read`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (!response.ok) throw new Error('Failed to mark read');
      return response.json();
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      if (options?.mutation?.onSuccess) {
        options.mutation.onSuccess(...args);
      }
    },
    ...options?.mutation
  });
};

export const useMarkAllNotificationsRead = (options?: UseMutationOptions<any, Error, void> | any) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, void>({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE}/notifications/mark-all-read`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (!response.ok) throw new Error('Failed to mark all read');
      return response.json();
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      if (options?.mutation?.onSuccess) {
        options.mutation.onSuccess(...args);
      }
    },
    ...options?.mutation
  });
};

export const useListPatients = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: options?.query?.queryKey || ['getListPatients', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/providers/patients?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch patients');
      const json = await res.json();
      return json.data;
    },
    enabled: options?.query?.enabled !== false
  });
};
export const useCreatePatient = (options?: any) => {
  return useMutation<any, Error, {
    firstName: string;
    middleName?: string;
    lastName: string;
    displayName?: string;
    email: string;
    phone?: string;
    whatsapp?: string;
    dob?: string;
    ssn?: string;
    gender?: string;
    languages?: string[];
    localityId?: string;
    localityIds?: string[];
    buildingNo?: string;
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }>({
    mutationFn: async (data) => {
      const res = await fetch(`${API_BASE}/providers/patients`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || 'Failed to add patient');
      }
      return json;
    },
    ...options
  });
};
export const useCheckPatientEmail = (options?: any) => {
  return useMutation<any, Error, { email: string }>({
    mutationFn: async ({ email }: { email: string }) => {
      const res = await fetch(`${API_BASE}/providers/check-email?email=${encodeURIComponent(email)}`, {
        headers: getHeaders()
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || 'Failed to check email');
      }
      return json;
    },
    ...options
  });
};
export const useRemovePatientFromCareTeam = (options?: any) => {
  return useMutation<any, Error, { patientId: string }>({
    mutationFn: async ({ patientId }: { patientId: string }) => {
      const res = await fetch(`${API_BASE}/providers/patients/${patientId}/care-team`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to remove patient from care team');
      return res.json();
    },
    ...options
  });
};

export const useSearchPatient = (queryStr: string, options?: any) => {
  return useQuery({
    queryKey: ['searchPatient', queryStr],
    queryFn: async () => {
      if (!queryStr || queryStr.trim().length === 0) return [];
      const res = await fetch(`${API_BASE}/providers/patients/search-patient?query=${encodeURIComponent(queryStr)}`, {
        headers: getHeaders()
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || 'Failed to search patient');
      }
      return json.data || [];
    },
    enabled: !!queryStr && queryStr.trim().length > 0,
    ...options?.query
  });
};

export const useRequestCareTeamLink = (options?: any) => {
  return useMutation<any, Error, { patientUserId: string; message?: string }>({
    mutationFn: async (payload) => {
      const res = await fetch(`${API_BASE}/providers/patients/request-care-team`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || 'Failed to send care team request');
      }
      return json;
    },
    ...options
  });
};

export const useInvitePatientEmail = (options?: any) => {
  return useMutation<any, Error, { email: string; name?: string }>({
    mutationFn: async (payload) => {
      const res = await fetch(`${API_BASE}/providers/patients/invite-patient`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || 'Failed to send invitation');
      }
      return json;
    },
    ...options
  });
};

export const useGetPendingCareTeamRequests = (options?: any) => {
  return useQuery({
    queryKey: ['getPendingCareTeamRequests'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/care-team/pending-requests`, {
        headers: getHeaders()
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Failed to fetch care team requests');
      }
      return json.data || [];
    },
    ...options?.query
  });
};

export const useRespondCareTeamRequest = (options?: any) => {
  return useMutation<any, Error, { id: string; action: 'ACCEPT' | 'REJECT' }>({
    mutationFn: async ({ id, action }) => {
      const res = await fetch(`${API_BASE}/v1/care-team/requests/${id}/respond`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action })
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Failed to respond to request');
      }
      return json;
    },
    ...options
  });
};

export const getListPatientsQueryKey = (...args: any[]) => ['getListPatients'];

export const useListProviders = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: options?.query?.queryKey || ['getListProviders', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/providers/search?${qs}`);
      if (!res.ok) throw new Error('Failed to fetch providers');
      const json = await res.json();
      return json.data;
    },
    enabled: options?.query?.enabled !== false
  });
};

export const useGetProviderById = (id?: string | null) => {
  return useQuery({
    queryKey: ['getProviderById', id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/providers/${id}`);
      if (!res.ok) throw new Error('Failed to fetch provider');
      const json = await res.json();
      return json.data;
    },
    enabled: !!id,
  });
};

export const useListSpecialties = () => {
  return useQuery({
    queryKey: ['getListSpecialties'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/provider-types`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch provider types');
      const json = await res.json();
      return json.data;
    }
  });
};

export const useGetCodeTypes = () => {
  return useQuery({
    queryKey: ['getCodeTypes'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/code-types`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch code types');
      const json = await res.json();
      return json.data || [];
    }
  });
};


export const useGetCptCategories = () => {
  return useQuery({
    queryKey: ['getCptCategories'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/cpt-categories`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch CPT categories');
      const json = await res.json();
      const raw = json.data;
      if (Array.isArray(raw)) {
        return raw.map((cat: any) => typeof cat === 'string' ? cat : String(cat?.name || ''));
      }
      return raw || [];
    },
    staleTime: 0,
    refetchOnMount: true
  });
};

export const useGetCptSubcategories = () => {
  return useQuery({
    queryKey: ['getCptSubcategories'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/cpt-subcategories`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch CPT subcategories');
      const json = await res.json();
      return json.data || [];
    },
    staleTime: 0,
    refetchOnMount: true
  });
};

export const useListTopRatedProviders = (...args: any[]) => ({ data: [] as any[], isLoading: false, error: null as any });

export const getListCostEstimatesQueryKey = (...args: any[]) => ['getListCostEstimates'];

export const useListCostEstimates = (...args: any[]) => {
  return useQuery({
    queryKey: getListCostEstimatesQueryKey(),
    queryFn: async () => {
      const saved = localStorage.getItem("mock_cost_estimates");
      return saved ? JSON.parse(saved) : [];
    }
  });
};

export const useCreateCostEstimate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: any) => {
      const payload = vars.data || vars;
      try {
        const res = await fetch(`${API_BASE}/members/estimates`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const json = await res.json();
          return json.data;
        }
      } catch (err) {
        console.warn('Backend estimate save fallback to local:', err);
      }

      // Local fallback storage
      const saved = localStorage.getItem("mock_cost_estimates") || "[]";
      const estimates = JSON.parse(saved);
      const newEstimate = {
        id: Date.now(),
        procedure: payload.procedure || payload.name || "Custom Estimate",
        cptCodes: payload.cptCodes || "00840",
        providerName: payload.providerName || "Selected Provider",
        providerType: payload.providerType || "Specialist",
        providerId: payload.providerId,
        insuranceCoverage: payload.insuranceCoverage || 0,
        patientResponsibility: payload.patientResponsibility || payload.estimatedCost || 6000,
        estimatedCost: payload.estimatedCost || 6000,
        insurancePlan: payload.insurancePlan || "StraightDoc"
      };
      estimates.push(newEstimate);
      localStorage.setItem("mock_cost_estimates", JSON.stringify(estimates));
      return newEstimate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['memberEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['providerEstimates'] });
    }
  });
};


export const useDeleteCostEstimate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: any) => {
      try {
        const res = await fetch(`${API_BASE}/members/estimates/${vars.id}`, {
          method: 'DELETE',
          headers: getHeaders()
        });
        if (!res.ok) {
          await fetch(`${API_BASE}/providers/estimates/${vars.id}`, {
            method: 'DELETE',
            headers: getHeaders()
          });
        }
      } catch (err) {
        // Fallback for local estimates
      }
      const saved = localStorage.getItem("mock_cost_estimates") || "[]";
      const estimates = JSON.parse(saved).filter((e: any) => String(e.id) !== String(vars.id));
      localStorage.setItem("mock_cost_estimates", JSON.stringify(estimates));
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['memberEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['providerEstimates'] });
    }
  });
};
export const useBulkAddProviders = () => {
  return useMutation({
    mutationFn: async ({ providers }: { providers: any[] }) => {
      const res = await fetch(`${API_BASE}/providers/bulk-add`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ providers })
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to bulk add providers');
      }
      return res.json();
    }
  });
};

export const useAddDetailedProvider = () => {
  return useMutation({
    mutationFn: async (provider: any) => {
      const res = await fetch(`${API_BASE}/providers/add-detailed`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(provider)
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to add provider');
      }
      return res.json();
    }
  });
};

export const useGetLanguages = () => {
  return useQuery({
    queryKey: ['getLanguages'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/languages`);
      if (!res.ok) throw new Error('Failed to fetch languages');
      const json = await res.json();
      return json.data;
    },
    staleTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};

export const useGetProviderTypes = () => {
  return useQuery({
    queryKey: ['getProviderTypes'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/provider-types`);
      if (!res.ok) throw new Error('Failed to fetch provider types');
      const json = await res.json();
      return json.data;
    },
    staleTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};

export const useGetVisitTypes = () => {
  return useQuery({
    queryKey: ['getVisitTypes'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/visit-types`);
      if (!res.ok) throw new Error('Failed to fetch visit types');
      const json = await res.json();
      return json.data;
    },
    staleTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};

export const useGetCredentialTypes = () => {
  return useQuery({
    queryKey: ['getCredentialTypes'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/master-data/credential-types`);
      if (!res.ok) throw new Error('Failed to fetch credential types');
      const json = await res.json();
      return json.data;
    },
    staleTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};

export const useGetLocalities = (state?: string | string[]) => {
  const stateQuery = Array.isArray(state) ? state.join(',') : (state || '');
  return useQuery({
    queryKey: ['getLocalities', stateQuery],
    queryFn: async () => {
      const url = stateQuery ? `${API_BASE}/auth/localities?state=${encodeURIComponent(stateQuery)}` : `${API_BASE}/auth/localities`;
      let res = await fetch(url);
      if (!res.ok) {
        const fallbackUrl = stateQuery ? `${API_BASE}/master-data/localities?state=${encodeURIComponent(stateQuery)}` : `${API_BASE}/master-data/localities`;
        res = await fetch(fallbackUrl);
      }
      if (!res.ok) throw new Error('Failed to fetch localities');
      const json = await res.json();
      return json.data;
    },
    staleTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};

export const useGetStates = () => {
  return useQuery({
    queryKey: ['getStates'],
    queryFn: async () => {
      let res = await fetch(`${API_BASE}/auth/states`);
      if (!res.ok) {
        res = await fetch(`${API_BASE}/master/states`);
      }
      if (!res.ok) throw new Error('Failed to fetch states');
      const json = await res.json();
      const raw = json.data;
      if (Array.isArray(raw)) {
        return raw.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || s?.id || ''));
      }
      return raw || [];
    },
    staleTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};

export const useUpdateUserLocalities = () => {
  return useMutation({
    mutationFn: async (data: { localityIds: string[] }) => {
      const res = await fetch(`${API_BASE}/auth/update-localities`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || err.message || 'Failed to update localities');
      }
      return res.json();
    }
  });
};

export const useInviteGroupProvider = () => {
  return useMutation({
    mutationFn: async (data: { email: string; npiNumber: string; percentage?: number; categoryRates?: Record<string, any> }) => {
      const res = await fetch(`${API_BASE}/providers/group/invitations`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.details ? `${json.error}: ${json.details}` : (json.error || 'Failed to send invitation'));
      }
      return res.json();
    }
  });
};

export const getListGroupInvitationsQueryKey = () => ['listGroupInvitations'];
export const useListGroupInvitations = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery<any, Error>({
    queryKey: getListGroupInvitationsQueryKey(),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/providers/group/invitations`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch group invitations');
      return res.json();
    },
    ...options,
  });
};

export const useDeleteGroupInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/providers/group/invitations/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to delete invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListGroupInvitationsQueryKey() });
    },
  });
};

export const useUpdateGroupInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, percentage, categoryRates }: { id: string; percentage?: number; categoryRates?: Record<string, any> }) => {
      const res = await fetch(`${API_BASE}/providers/group/invitations/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ percentage, categoryRates })
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to update invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListGroupInvitationsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['myGroupInvitations'] });
    },
  });
};

export const getMyInvitationsQueryKey = (userId?: string) => ['myGroupInvitations', userId];

// ========================
// Review API
// ========================

export const useGetReviewContext = (token: string, options?: any) => {
  return useQuery({
    queryKey: ['review-context', token],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/reviews/token/${token}`, { headers: getHeaders() });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch review context');
      }
      return res.json();
    },
    ...options?.query
  });
};

export const useSubmitReview = (options?: any) => {
  return useMutation<any, Error, { token: string; rating: number; comment?: string }>({
    mutationFn: async (data) => {
      const res = await fetch(`${API_BASE}/reviews/submit`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: getHeaders()
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit review');
      }
      return res.json();
    },
    ...options
  });
};

export const useGetProviderReviews = (providerId: string, filter: string = 'all', page: number = 1, limit: number = 10, options?: any) => {
  return useQuery({
    queryKey: ['provider-reviews', providerId, filter, page, limit],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/reviews/provider/${providerId}?filter=${filter}&page=${page}&limit=${limit}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch provider reviews');
      return res.json();
    },
    ...options?.query
  });
};

export const useGetMyReviews = (page: number = 1, limit: number = 10, options?: any) => {
  return useQuery({
    queryKey: ['my-reviews', page, limit],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/reviews/my?page=${page}&limit=${limit}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch my reviews');
      return res.json();
    },
    ...options?.query
  });
};

export const useGetMyInvitations = (userId?: string, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery<any, Error>({
    queryKey: getMyInvitationsQueryKey(userId),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/providers/my-invitations`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch your invitations');
      return res.json();
    },
    ...options,
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/providers/my-invitations/${id}/accept`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to accept invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myGroupInvitations'] });
    },
  });
};

export const useRejectInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/providers/my-invitations/${id}/reject`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to reject invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myGroupInvitations'] });
    },
  });
};

export const useLeaveGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string = 'current') => {
      const res = await fetch(`${API_BASE}/providers/my-invitations/${id}/leave`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to leave group practice');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myGroupInvitations'] });
      queryClient.invalidateQueries({ queryKey: ['providerAppointments'] });
    },
  });
};

// --- Appointments ---

export const useBookAppointment = () => {
  return useMutation({
    mutationFn: async (data: { providerId: string; bundleId?: string; notes?: string; amount?: number; groupId?: string; appointmentDate?: string; startTime?: string; endTime?: string; [key: string]: any }) => {
      const res = await fetch(`${API_BASE}/appointments/book`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to book appointment');
      return res.json();
    }
  });
};

export const getMemberAppointmentsQueryKey = () => ['memberAppointments'];
export const useGetMemberAppointments = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: ['getMemberAppointments', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/appointments/member?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch member appointments');
      const json = await res.json();
      return json.data;
    },
    ...options?.query
  });
};

export const useGetProviderAppointmentFilterOptions = (options?: any) => {
  return useQuery({
    queryKey: ['getProviderAppointmentFilterOptions'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/appointments/provider/filter-options`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch provider appointment filter options');
      const json = await res.json();
      return json.data;
    },
    ...options?.query
  });
};

export const getProviderAppointmentsQueryKey = () => ['getProviderAppointments'];
export const useGetProviderAppointments = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: ['getProviderAppointments', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/appointments/provider?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch provider appointments');
      const json = await res.json();
      return json.data;
    },
    ...options?.query
  });
};

export const useUpdateAppointmentStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, amount, notes, serviceRenderedDate }: { id: string; status?: string; amount?: number; notes?: string; serviceRenderedDate?: string }) => {
      const res = await fetch(`${API_BASE}/appointments/${id}/status`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ status, amount, notes, serviceRenderedDate }),
      });
      if (!res.ok) throw new Error('Failed to update appointment');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProviderAppointmentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getMemberAppointmentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['getGroupAppointments'] });
    },
  });
};

export const useStartAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, verificationCode }: { id: string; verificationCode: string }) => {
      const res = await fetch(`${API_BASE}/appointments/${id}/start`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ verificationCode }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to start appointment');
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProviderAppointmentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getMemberAppointmentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['getGroupAppointments'] });
    },
  });
};

export const useInviteProviderToPortal = () => {
  return useMutation({
    mutationFn: async ({ id, patientName, patientEmail, message }: { id: string; patientName?: string; patientEmail?: string; message?: string }) => {
      const res = await fetch(`${API_BASE}/providers/${id}/invite-to-portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patientName, patientEmail, message })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to send invitation");
      return json;
    }
  });
};

export const useGetGroupAppointments = (params?: Record<string, any>, options?: any) => {
  return useQuery({
    queryKey: ['getGroupAppointments', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) searchParams.append(key, String(value));
        });
      }
      const qs = searchParams.toString();
      const res = await fetch(`${API_BASE}/appointments/group?${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch group appointments');
      const json = await res.json();
      return json.data;
    },
    ...options?.query
  });
};

export const useListAgentMembers = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['agentMembers'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/members`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch connected members');
      return res.json().then(d => d.data);
    },
    ...options
  });
};

export const useCreateAgentMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${API_BASE}/agent/members`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to enroll member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentMembers'] });
    }
  });
};

export const useUpdateAgentMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await fetch(`${API_BASE}/agent/members/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to update member personal information');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentMembers'] });
    }
  });
};

export const useListAgentProviders = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['agentProviders'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/providers`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch connected providers');
      return res.json().then(d => d.data);
    },
    ...options
  });
};

// ========== AGENT PROFILE ========== //

export function useGetAgentProfile() {
  return useQuery({
    queryKey: ['agentProfile'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/profile`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch agent profile');
      return res.json().then(d => d.data);
    }
  });
}

export function useUpdateAgentProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${API_BASE}/agent/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to update agent profile');
      return res.json().then(d => d.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentProfile'] });
    }
  });
}

// ========== AGENT CONNECT (Member & Provider) ========== //

export function useSearchAgents() {
  const token = typeof window !== 'undefined' ? (localStorage.getItem('token') || '') : '';
  return useQuery({
    queryKey: ['searchAgents', token],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/search`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch agents');
      return res.json().then(d => d.data);
    }
  });
}

export function useConnectAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetch(`${API_BASE}/agent/${agentId}/connect`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to connect to agent');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searchAgents'] });
      // Might want to invalidate user profile or dashboard queries where the agent is displayed
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });
}

export function useDisconnectAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await fetch(`${API_BASE}/agent/${agentId}/disconnect`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to disconnect from agent');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searchAgents'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });
}

// ========== AGENT INVITATIONS ========== //

export const useInviteAgent = () => {
  return useMutation({
    mutationFn: async (data: { email: string; agentNumber: string }) => {
      const res = await fetch(`${API_BASE}/agent/invitations`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.details ? `${json.error}: ${json.details}` : (json.error || 'Failed to send invitation'));
      }
      return res.json();
    }
  });
};

export const getListAgentInvitationsQueryKey = () => ['listAgentInvitations'];
export const useListAgentInvitations = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery<any, Error>({
    queryKey: getListAgentInvitationsQueryKey(),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/invitations`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch agent invitations');
      return res.json();
    },
    ...options,
  });
};

export const useDeleteAgentInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/agent/invitations/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to delete invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListAgentInvitationsQueryKey() });
    },
  });
};

export const getMyAgentInvitationsQueryKey = (userId?: string) => ['myAgentInvitations', userId];
export const useGetMyAgentInvitations = (userId?: string, options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery<any, Error>({
    queryKey: getMyAgentInvitationsQueryKey(userId),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/my-invitations`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch your agent invitations');
      return res.json();
    },
    ...options,
  });
};

export const useAcceptAgentInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/agent/invitations/${id}/accept`, {
        method: 'PUT',
        headers: getHeaders(),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to accept invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getMyAgentInvitationsQueryKey() });
    },
  });
};

export const useSubmitProviderEstimate = () => {
  return useMutation({
    mutationFn: async (data: { title?: string; name?: string; description?: string; memberId?: string; totalCharges: number; discount: number; finalAmount: number; items: any; status?: string }) => {
      const res = await fetch(`${API_BASE}/providers/estimates`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create estimate');
      }
      return res.json();
    }
  });
};

export const useListProviderEstimates = () => {
  return useQuery({
    queryKey: ['providerEstimates'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/providers/estimates`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch estimates');
      const json = await res.json();
      return json.data;
    }
  });
};

export const useListMemberEstimates = () => {
  return useQuery({
    queryKey: ['memberEstimates'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/members/estimates`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch estimates');
      const json = await res.json();
      return json.data;
    }
  });
};

export const useUpdateEstimateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      finalAmount, 
      discount, 
      totalCharges, 
      items, 
      counterNote, 
      negotiationNote, 
      comment, 
      paymentMethod, 
      paymentDate, 
      serviceRenderedDate, 
      serviceDate, 
      paymentNote, 
      paymentReference 
    }: { 
      id: string; 
      status: string; 
      finalAmount?: number; 
      discount?: number; 
      totalCharges?: number; 
      items?: any[]; 
      counterNote?: string; 
      negotiationNote?: string; 
      comment?: string; 
      paymentMethod?: string; 
      paymentDate?: string; 
      serviceRenderedDate?: string; 
      serviceDate?: string; 
      paymentNote?: string; 
      paymentReference?: string; 
    }) => {
      const res = await fetch(`${API_BASE}/providers/estimates/${id}/status`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ 
          status, 
          finalAmount, 
          discount, 
          totalCharges, 
          items, 
          counterNote, 
          negotiationNote, 
          comment, 
          paymentMethod, 
          paymentDate, 
          serviceRenderedDate, 
          serviceDate, 
          paymentNote, 
          paymentReference 
        })
      });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providerEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['memberEstimates'] });
    }
  });
};

export const useRedeemVoucherCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ redemptionCode, estimateId }: { redemptionCode?: string; estimateId?: string }) => {
      const res = await fetch(`${API_BASE}/provider/estimates/redeem-voucher`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ redemptionCode, estimateId })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to redeem voucher code');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
    }
  });
};

export const useUpdateMemberEstimateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      finalAmount, 
      discount, 
      totalCharges, 
      items, 
      counterNote, 
      negotiationNote, 
      comment, 
      targetEstimate, 
      paymentMethod, 
      paymentDate, 
      serviceRenderedDate, 
      serviceDate, 
      paymentNote, 
      paymentReference 
    }: { 
      id: string; 
      status: string; 
      finalAmount?: number; 
      discount?: number; 
      totalCharges?: number; 
      items?: any[]; 
      counterNote?: string; 
      negotiationNote?: string; 
      comment?: string; 
      targetEstimate?: any; 
      paymentMethod?: string; 
      paymentDate?: string; 
      serviceRenderedDate?: string; 
      serviceDate?: string; 
      paymentNote?: string; 
      paymentReference?: string; 
    }) => {
      try {
        const res = await fetch(`${API_BASE}/members/estimates/${id}/status`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({ 
            status, 
            finalAmount, 
            discount, 
            totalCharges, 
            items, 
            counterNote, 
            negotiationNote, 
            comment, 
            paymentMethod, 
            paymentDate, 
            serviceRenderedDate, 
            serviceDate, 
            paymentNote, 
            paymentReference 
          })
        });
        if (res.ok) {
          const json = await res.json();
          return json.data;
        }
      } catch (err) {
        console.warn('Backend update failed, attempting sync or fallback:', err);
      }

      // If backend returned error (e.g. local estimate not yet in DB), create it on backend!
      if (targetEstimate) {
        try {
          const createRes = await fetch(`${API_BASE}/members/estimates`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
              providerId: targetEstimate.providerId || targetEstimate.provider?.id || targetEstimate.provider?.userId,
              name: targetEstimate.name || targetEstimate.title || targetEstimate.procedure || 'Custom Estimate',
              totalCharges: totalCharges != null ? totalCharges : (targetEstimate.totalCharges || targetEstimate.estimatedCost || finalAmount || 0),
              discount: discount != null ? discount : (targetEstimate.discount || 0),
              finalAmount: finalAmount != null ? finalAmount : (targetEstimate.finalAmount || targetEstimate.patientResponsibility || 0),
              items: items || targetEstimate.items || [],
              status: status,
              patientNote: counterNote || negotiationNote || ''
            })
          });
          if (createRes.ok) {
            const json = await createRes.json();
            return json.data;
          }
        } catch (err) {
          console.warn('Estimate backend create sync error:', err);
        }
      }

      // Fallback: update in localStorage mock_cost_estimates
      const saved = localStorage.getItem("mock_cost_estimates") || "[]";
      const estimates = JSON.parse(saved);
      const updated = estimates.map((e: any) => {
        if (String(e.id) === String(id)) {
          return {
            ...e,
            status,
            finalAmount: finalAmount != null ? finalAmount : e.finalAmount,
            discount: discount != null ? discount : e.discount,
            items: items || e.items,
            patientNotes: counterNote || negotiationNote || e.patientNotes,
            paymentDetails: paymentMethod ? { paymentMethod, paymentDate: paymentDate || new Date().toISOString(), paymentNote, paymentReference, amountPaid: finalAmount || e.finalAmount } : e.paymentDetails
          };
        }
        return e;
      });
      localStorage.setItem("mock_cost_estimates", JSON.stringify(updated));
      return { id, status };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['memberEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['providerEstimates'] });
    }
  });
};

export const useAssignEstimateToPatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, memberId }: { id: string; memberId: string }) => {
      const res = await fetch(`${API_BASE}/providers/estimates/${id}/assign-patient`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ memberId })
      });
      if (!res.ok) throw new Error('Failed to assign patient to estimate');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providerEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['memberEstimates'] });
    }
  });
};

export const useUpdateCostEstimateDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; title?: string; name?: string; description?: string; memberId?: string; items?: any[]; totalCharges?: number; discount?: number; finalAmount?: number; status?: string }) => {
      const { id, ...payload } = data;
      let res = await fetch(`${API_BASE}/providers/estimates/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        res = await fetch(`${API_BASE}/members/estimates/${id}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(payload)
        });
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to update estimate details');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providerEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['memberEstimates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/cost-estimates'] });
      queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
    }
  });
};

export const useGetSavedBundles = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['savedBundles'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/members/saved-bundles`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch saved bundles');
      return res.json().then(d => d.data);
    },
    ...options
  });
};

export const useGetSavedBundleIds = (options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['savedBundleIds'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/members/saved-bundles/ids`, {
        headers: getHeaders()
      });
      if (!res.ok) return [];
      return res.json().then(d => d.data || []);
    },
    ...options
  });
};

export const useToggleSavedBundle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bundleId: string) => {
      const res = await fetch(`${API_BASE}/members/saved-bundles/toggle`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ bundleId })
      });
      if (!res.ok) throw new Error('Failed to save bundle');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedBundles'] });
      queryClient.invalidateQueries({ queryKey: ['savedBundleIds'] });
    }
  });
};

export const useGetAgentMemberCareTeam = (memberId: string, options?: any) => {
  return useQuery({
    queryKey: ['agentMemberCareTeam', memberId],
    queryFn: async () => {
      if (!memberId) return [];
      const res = await fetch(`${API_BASE}/agent/members/${memberId}/care-team`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch member care team');
      const data = await res.json();
      return data.data || [];
    },
    enabled: Boolean(memberId),
    ...options
  });
};

export const useAddAgentMemberCareTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId, providerId }: { memberId: string; providerId: string }) => {
      const res = await fetch(`${API_BASE}/agent/members/${memberId}/care-team`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ providerId })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to add care team provider');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agentMemberCareTeam', variables.memberId] });
    }
  });
};

export const useRemoveAgentMemberCareTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId, providerId }: { memberId: string; providerId: string }) => {
      const res = await fetch(`${API_BASE}/agent/members/${memberId}/care-team/${providerId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to remove care team provider');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agentMemberCareTeam', variables.memberId] });
    }
  });
};

export const useSearchProvidersForCareTeam = (query?: string, options?: any) => {
  return useQuery({
    queryKey: ['searchProvidersForCareTeam', query],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/agent/providers/search?query=${encodeURIComponent(query || '')}`, {
        headers: getHeaders()
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.data || [];
    },
    ...options
  });
};

export const useSendAgentMemberIdCardEmail = () => {
  return useMutation({
    mutationFn: async ({ memberId, recipientEmail, customNote, cardDataUrl }: { memberId: string; recipientEmail?: string; customNote?: string; cardDataUrl?: string }) => {
      const res = await fetch(`${API_BASE}/agent/members/${memberId}/send-id-card-email`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ recipientEmail, customNote, cardDataUrl })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to send ID card email');
      }
      return res.json();
    }
  });
};

export const useSendAgentMemberIdCardSms = () => {
  return useMutation({
    mutationFn: async ({ memberId, recipientPhone, customNote }: { memberId: string; recipientPhone?: string; customNote?: string }) => {
      const res = await fetch(`${API_BASE}/agent/members/${memberId}/send-id-card-sms`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ recipientPhone, customNote })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to send ID card text');
      }
      return res.json();
    }
  });
};

export const getHouseholdQueryKey = () => ['getHousehold'];

export const useGetHousehold = (options?: any) => {
  return useQuery({
    queryKey: getHouseholdQueryKey(),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/household`, {
        headers: getHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch household');
      const json = await res.json();
      return json.data;
    },
    ...options
  });
};

export const useAddHouseholdMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      dob?: string;
      gender?: string;
      relationship: string;
      accountType: 'SEPARATE_ACCOUNT' | 'MANAGED_DEPENDENT';
    }) => {
      const res = await fetch(`${API_BASE}/household/members`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to add household member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getHouseholdQueryKey() });
    }
  });
};

export const useUpdateHouseholdMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; firstName?: string; lastName?: string; relationship?: string; accountType?: string; phone?: string; dob?: string; gender?: string; }) => {
      const res = await fetch(`${API_BASE}/household/members/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update household member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getHouseholdQueryKey() });
    }
  });
};

export const useDeleteHouseholdMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/household/members/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to remove household member');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getHouseholdQueryKey() });
    }
  });
};

